import { customElement, state } from "lit/decorators.js";
import { html, LitElement, nothing, PropertyValues } from "lit";
import { consume } from "@lit/context";
import { filterContext, IFilterContext } from "./filterContext";
import { replaceElementWithHtml } from "../../util/dom";

@customElement("ehm-filter-facets")
export class Facets extends LitElement {
  @state()
  @consume({ context: filterContext, subscribe: true })
  context?: IFilterContext;

  private _inputFields: NodeListOf<HTMLInputElement> | null = null;
  private _detailsElements: NodeListOf<HTMLDetailsElement> | null = null;
  private _resetButton: HTMLButtonElement | null = null;

  constructor() {
    super();
    this.setAttribute("aria-live", "polite");

  }

  handleInputChange = (e: Event) => {
    const input = e.target as HTMLInputElement;
    this.context?.updateFilters(
      {
        name: input.dataset.label || input.value,
        value: input.value,
        param: input.name
      },
      input.checked ? "add" : "remove"
    );
    this.hydrate();
  };

  hydrate() {
    this._inputFields?.forEach(input => {
      const filter = this.context?.filters?.find(f => f.param === input.name && f.value === input.value && !input.disabled);
      input.checked = !!filter;
    });

    const resetButton = this.querySelector("button[data-action=\"clear-facets\"]");
    if (!this.context?.filters || this.context?.filters.length === 0) {
      resetButton?.setAttribute("disabled", "disabled");
    } else {
      resetButton?.removeAttribute("disabled");

    }
  }

  updated(_changedProperties: PropertyValues) {
    const prev = _changedProperties.get("context") as IFilterContext | undefined;
    if (prev?.result?.facets && prev.result.facets === this.context?.result?.facets) {
      return;
    }

    this.setAttribute("aria-loading", this.context?.isLoading ? "true" : "false");
    const facetsHtml = this.context?.result?.facets;
    const container = this.querySelector("#facets-container");
    if (container && facetsHtml) {
      replaceElementWithHtml(facetsHtml, container, true);
    }
    this._inputFields = this.querySelectorAll("input[type=\"checkbox\"]");
    this._inputFields?.forEach(input => {
      input.addEventListener("change", this.handleInputChange);
    });
    this._detailsElements = this.querySelectorAll("details");
    this._detailsElements?.forEach(element => {
      element.addEventListener("toggle", this.handleDetailsToggle);
    });
    this._resetButton = this.querySelector("button[data-action=\"clear-facets\"]");
    this._resetButton?.addEventListener("click", () => {
      this.context?.resetFacets();
      setTimeout(() => {
        this.hydrate();
      });
    });
    this.hydrate();
  }

  handleDetailsToggle = (e: Event) => {
    const detailsElement = e.target as HTMLDetailsElement;
    if (!detailsElement.id) return;
    const url = new URL(window.location.href);
    if (detailsElement.open) {
      if (!url.searchParams.has("open", detailsElement.id)) {
        url.searchParams.append("open", detailsElement.id);
      }
    } else {
      url.searchParams.delete("open", detailsElement.id);
    }
    window.history.replaceState({}, "", url.toString());
  };

  protected render() {
    if (this.context?.total === 0) {
      return nothing;
    }
    return html`
      <slot></slot>
    `;
  }
}
